/**
 * Custom Web Component for How It Work Slider
 * Handles Swiper initialization with responsive behavior and navigation
 */
class HowItWorkSlider extends HTMLElement {
  constructor() {
    super();
    this.swiperInstance = null;
    this.resizeHandler = null;
  }

  connectedCallback() {
    // Wait for DOM to be ready before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    this.destroy();
  }

  /**
   * Initialize the slider component
   */
  init() {
    try {
      this.setupConfiguration();
      this.setupNavigation();
      this.initializeSwiper();
      this.bindEvents();
    } catch (error) {
      console.error('Error initializing How It Work Slider:', error);
    }
  }

  /**
   * Extract configuration from data attributes
   */
  setupConfiguration() {
    this.config = {
      desktopSlidesPerView: parseFloat(this.dataset.desktopSlidesPerView) || 3,
      mobileSlidesPerView: parseFloat(this.dataset.mobileSlidesPerView) || 1.2,
      gapMobile: parseInt(this.dataset.gapMobile) || 10,
      gapDesktop: parseInt(this.dataset.gapDesktop) || 20,
      blocksSize: parseInt(this.dataset.blocksSize) || 0,
      desktopColumns: parseInt(this.dataset.desktopColumns) || 3,
      sectionId: this.dataset.sectionId || ''
    };
  }

  /**
   * Setup navigation elements and visibility logic
   */
  setupNavigation() {
    this.navDesktop = this.querySelector('.swiper-nav-desktop');
    this.prevButton = this.querySelector('.slider-button--prev');
    this.nextButton = this.querySelector('.slider-button--next');

    // Create bound method for resize handler
    this.resizeHandler = this.handleResize.bind(this);
    
    this.updateNavigationVisibility();
  }

  /**
   * Initialize Swiper instance with configuration
   */
  initializeSwiper() {
    const swiperContainer = this.querySelector('.swiper-container');
    
    if (!swiperContainer) {
      console.warn('Swiper container not found');
      return;
    }

    // Destroy existing instance if present
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }

    const swiperConfig = {
      slidesPerView: this.config.mobileSlidesPerView,
      spaceBetween: this.config.gapMobile,
      grabCursor: true,
      navigation: {
        prevEl: this.prevButton,
        nextEl: this.nextButton,
      },
      breakpoints: {
        640: {
          slidesPerView: 2.2,
          spaceBetween: this.config.gapDesktop,
        },
        992: {
          slidesPerView: this.config.desktopSlidesPerView,
          spaceBetween: this.config.gapDesktop,
        }
      },
      // Add accessibility features
      a11y: {
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        firstSlideMessage: 'This is the first slide',
        lastSlideMessage: 'This is the last slide',
      }
    };

    this.swiperInstance = new Swiper(swiperContainer, swiperConfig);
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    this.updateNavigationVisibility();
  }

  /**
   * Update navigation visibility based on content and screen size
   */
  updateNavigationVisibility() {
    if (this.navDesktop) {
      const shouldShowNav = this.config.blocksSize > this.config.desktopColumns;
      this.navDesktop.style.display = shouldShowNav ? 'flex' : 'none';
    }
  }

  /**
   * Destroy the component and clean up
   */
  destroy() {
    // Remove event listeners
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }

    // Destroy Swiper instance
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  /**
   * Reinitialize the slider (useful for dynamic content updates)
   */
  reinitialize() {
    this.destroy();
    this.init();
  }

  /**
   * Update slides per view (useful for dynamic updates)
   */
  updateSlidesPerView(desktop, mobile) {
    if (this.swiperInstance) {
      this.config.desktopSlidesPerView = desktop || this.config.desktopSlidesPerView;
      this.config.mobileSlidesPerView = mobile || this.config.mobileSlidesPerView;
      
      // Update Swiper configuration
      this.swiperInstance.params.slidesPerView = this.config.mobileSlidesPerView;
      this.swiperInstance.params.breakpoints[992].slidesPerView = this.config.desktopSlidesPerView;
      
      this.swiperInstance.update();
      this.updateNavigationVisibility();
    }
  }

  /**
   * Get current slide index
   */
  getCurrentSlide() {
    return this.swiperInstance ? this.swiperInstance.activeIndex : 0;
  }

  /**
   * Go to specific slide
   */
  goToSlide(index, speed = 300) {
    if (this.swiperInstance) {
      this.swiperInstance.slideTo(index, speed);
    }
  }
}

/**
 * Shopify Theme Editor Integration
 */
class HowItWorkManager {
  static init() {
    // Initialize all existing sliders on page load
    document.addEventListener('DOMContentLoaded', () => {
      HowItWorkManager.initializeAll();
    });

    // Handle Shopify theme editor events
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', HowItWorkManager.handleSectionLoad);
      document.addEventListener('shopify:section:unload', HowItWorkManager.handleSectionUnload);
      document.addEventListener('shopify:section:reorder', HowItWorkManager.handleSectionReorder);
    }
  }

  static initializeAll() {
    const sliders = document.querySelectorAll('how-it-work-slider');
    sliders.forEach(slider => {
      if (!slider.swiperInstance) {
        slider.init();
      }
    });
  }

  static handleSectionLoad(event) {
    const slider = event.target.querySelector('how-it-work-slider');
    if (slider) {
      // Small delay to ensure DOM is ready
      setTimeout(() => slider.init(), 100);
    }
  }

  static handleSectionUnload(event) {
    const slider = event.target.querySelector('how-it-work-slider');
    if (slider) {
      slider.destroy();
    }
  }

  static handleSectionReorder(event) {
    // Reinitialize all sliders after reorder
    setTimeout(() => HowItWorkManager.initializeAll(), 200);
  }
}

// Define the custom element
if (customElements && !customElements.get('how-it-work-slider')) {
  customElements.define('how-it-work-slider', HowItWorkSlider);
}

// Initialize the manager
HowItWorkManager.init();

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { HowItWorkSlider, HowItWorkManager };
}
